/**
 * Collection comparison — table layout.
 *
 * Behaviour for sections/collection-comparison-table.liquid. Independent of the
 * CollectionComparison element in additional.js: different tag name, different class
 * names, no shared state.
 *
 * Responsibilities:
 *   - build the styled type dropdown on top of the real <select>
 *   - show/hide whole columns when the type changes, keeping the URL hash in sync
 *   - drag-to-scroll the table sideways with the mouse
 *   - keep the head bar's columns and horizontal scroll matched to the table it floats over
 */
class CollectionComparisonTable extends HTMLElement {
  connectedCallback() {
    // Children are only guaranteed to be parsed here, not in the constructor.
    if (this.initialized) return;
    this.initialized = true;

    this.sectionId = this.dataset.sectionId;
    this.scroller = this.querySelector('.comparison-table__scroller');
    this.table = this.scroller.querySelector('.comparison-table__table');
    this.headBar = this.querySelector('.comparison-table__head-bar');
    this.headScroller = this.querySelector('.comparison-table__head-scroller');
    this.headTable = this.querySelector('.comparison-table__table--head');
    this.select = this.querySelector('.comparison-table__type-dropdown-select');
    this.dropdown = this.querySelector('.comparison-table__type-dropdown');
    this.dropdownLayer = this.querySelector('.comparison-table__dropdown-layer');

    this.types = Array.from(this.select.options).map((option) => option.value);
    this.defaultType = this.types[0];

    this.onDocumentClick = this.closeDropdown.bind(this);
    this.onHashChange = this.applyHash.bind(this);
    this.onScrollerScroll = this.onScrollerScroll.bind(this);
    this.syncHeadBar = this.syncHeadBar.bind(this);
    this.requestHeadOffset = this.requestHeadOffset.bind(this);
    this.trackHeadOffset = this.trackHeadOffset.bind(this);

    this.buildDropdown();
    this.initDragScroll();
    this.initHeadBar();

    this.select.addEventListener('change', () => this.selectType(this.select.value, true));
    window.addEventListener('hashchange', this.onHashChange);
    document.addEventListener('click', this.onDocumentClick);

    this.applyHash();
  }

  disconnectedCallback() {
    if (!this.initialized) return;
    window.removeEventListener('hashchange', this.onHashChange);
    document.removeEventListener('click', this.onDocumentClick);
    this.scroller.removeEventListener('scroll', this.onScrollerScroll);
    window.removeEventListener('scroll', this.requestHeadOffset);
    if (this.siteHeader) {
      this.siteHeader.removeEventListener('transitionstart', this.requestHeadOffset);
      this.siteHeader.removeEventListener('transitionend', this.requestHeadOffset);
    }
    window.removeEventListener('load', this.syncHeadBar);
    window.removeEventListener('resize', this.syncHeadBar);
    if (this.headOffsetFrame) cancelAnimationFrame(this.headOffsetFrame);
  }

  /* ---------------------------------------------------------------- Dropdown */

  /**
   * The native <select> stays in the markup as the source of truth (and the thing the
   * `change` listener reacts to); these two divs are what the design shows.
   *
   * The value sits in the head cell, but the list goes in the bar's dropdown layer: the
   * cell is inside a scroll container that is only as tall as the head row, so a list left
   * in there would be clipped after the first option or two.
   */
  buildDropdown() {
    this.selected = document.createElement('div');
    this.selected.className = 'comparison-table__type-dropdown-selected';
    this.selected.textContent = this.select.options[this.select.selectedIndex].textContent;

    this.options = document.createElement('div');
    this.options.className =
      'comparison-table__type-dropdown-options comparison-table__type-dropdown-options--hidden';

    Array.from(this.select.options).forEach((option) => {
      const item = document.createElement('div');
      item.textContent = option.textContent;
      item.dataset.type = option.value;
      item.addEventListener('click', (event) => {
        event.stopPropagation();
        this.selectType(option.value, true);
        this.closeDropdown();
      });
      this.options.appendChild(item);
    });

    this.selected.addEventListener('click', (event) => {
      event.stopPropagation();
      // Measure before unhiding: a hidden element has no box to measure.
      this.positionOptions();
      this.options.classList.toggle('comparison-table__type-dropdown-options--hidden');
      this.selected.classList.toggle('comparison-table__type-dropdown-selected--active');
    });

    this.dropdown.appendChild(this.selected);
    this.dropdownLayer.appendChild(this.options);
  }

  /**
   * Line the list up under the selected value. Both sit in the head bar, so the offsets
   * are taken between them and hold for as long as the bar does — there is nothing to
   * recompute while the page or the table scrolls.
   */
  positionOptions() {
    const anchor = this.selected.getBoundingClientRect();
    const layer = this.dropdownLayer.getBoundingClientRect();
    this.options.style.left = anchor.left - layer.left + 'px';
    this.options.style.top = anchor.bottom - layer.top + 'px';
    // Published rather than applied as a width: the stylesheet adds the allowance for the
    // list's own horizontal padding, so that number sits next to the padding it pays for.
    this.options.style.setProperty('--comparisonTableDropdownAnchorWidth', anchor.width + 'px');
  }

  closeDropdown() {
    this.options.classList.add('comparison-table__type-dropdown-options--hidden');
    this.selected.classList.remove('comparison-table__type-dropdown-selected--active');
  }

  /* ------------------------------------------------------------ Column state */

  /**
   * Show every cell belonging to `type` and hide the rest. Each cell carries its own
   * data-comparison-collection-type, so one pass over the table covers both the head row
   * and the body rows.
   *
   * @param {String} type - collection comparison type code
   * @param {Boolean} updateHash - whether to reflect the choice in the URL
   */
  selectType(type, updateHash) {
    if (this.types.indexOf(type) === -1) return;

    // Covers the cells in the table and the duplicated head cells in the bar alike.
    this.querySelectorAll('[data-comparison-collection-type]').forEach((cell) => {
      const matches = cell.dataset.comparisonCollectionType === type;
      cell.classList.toggle('comparison-table__cell--hidden', !matches);
    });
    if (this.select.value !== type) this.select.value = type;
    this.selected.textContent = this.select.options[this.select.selectedIndex].textContent;

    this.options.querySelectorAll('div').forEach((item) => {
      item.classList.toggle('is-selected', item.dataset.type === type);
    });

    if (updateHash) history.replaceState(null, '', '#' + type);

    // The table's columns have just been re-laid out; the bar has to follow.
    this.syncHeadColumns();
  }

  applyHash() {
    const hash = window.location.hash ? window.location.hash.slice(1) : '';
    this.selectType(this.types.indexOf(hash) !== -1 ? hash : this.defaultType, false);
  }

  /* ------------------------------------------------------------- Scroll box */

  /**
   * The head bar floats over the table as an ordinary sticky element, so nothing here runs
   * during vertical scrolling. What it does have to do is keep the bar lined up with the
   * table it covers: same column widths, and the same horizontal scroll position.
   */
  initHeadBar() {
    this.siteHeader = document.querySelector('.site-header');
    this.headOffset = null;
    // The widest type decides the column width for every type, so the table keeps the same
    // column grid whichever filter is showing.
    this.maxColumns = this.countWidestType();

    this.syncHeadBar();
    this.scroller.addEventListener('scroll', this.onScrollerScroll, { passive: true });
    // The site header moves independently of the page, so where the bar has to park
    // changes as you scroll. See syncHeadOffset.
    window.addEventListener('scroll', this.requestHeadOffset, { passive: true });
    // It also finishes moving on its own, 0.4s after the last scroll event, so the
    // transition has to be followed as well or the bar stops short of it.
    if (this.siteHeader) {
      this.siteHeader.addEventListener('transitionstart', this.requestHeadOffset);
      this.siteHeader.addEventListener('transitionend', this.requestHeadOffset);
    }
    // theme.js may not have wrapped the site header yet when this element upgrades, and web
    // fonts can still change how wide the columns end up.
    window.addEventListener('load', this.syncHeadBar);
    window.addEventListener('resize', this.syncHeadBar);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.syncHeadBar);
  }

  /** Everything that only changes on load, resize or a change of type — never on scroll. */
  syncHeadBar() {
    this.syncHeadOffset();
    this.syncColumnWidth();
    this.syncHeadColumns();
    this.positionOptions();
  }

  /** How many collections the most populated type has. */
  countWidestType() {
    const counts = {};
    this.scroller.querySelectorAll('.comparison-table__cell--collection').forEach((cell) => {
      const type = cell.dataset.comparisonCollectionType;
      counts[type] = (counts[type] || 0) + 1;
    });
    const values = Object.values(counts);
    return values.length ? Math.max.apply(null, values) : 1;
  }

  /**
   * Work out the one column width every type will use, and publish it for the stylesheet.
   *
   * Sizing off the widest type rather than the visible one is what stops the columns
   * resizing under the reader when they change the filter: four collections and two now
   * draw the same grid, the shorter one just stops earlier. The configured content width is
   * the floor, so a viewport too narrow to give every column that much scrolls sideways
   * instead of squeezing them — which is what the width was there to guarantee before.
   */
  syncColumnWidth() {
    const styles = getComputedStyle(this);
    const label = parseFloat(styles.getPropertyValue('--comparisonTableLabelColumnWidth')) || 0;
    const minimum = parseFloat(styles.getPropertyValue('--comparisonTableValueColumnWidth')) || 0;
    const available = this.scroller.clientWidth;
    const share = (available - label) / this.maxColumns;
    this.style.setProperty('--comparisonTableColumnWidth', Math.max(minimum, share) + 'px');
  }

  /** Start following the header, if we are not already. */
  requestHeadOffset() {
    this.headOffsetSettled = 0;
    if (this.headOffsetFrame) return;
    this.headOffsetFrame = requestAnimationFrame(this.trackHeadOffset);
  }

  /**
   * Sample once a frame for as long as the header is still moving, then stop.
   *
   * A single measurement per scroll event is not enough: theme.js adds the class that
   * starts the header's 0.4s slide 100ms after the scroll that triggered it, so the header
   * is still travelling long after the last scroll event has fired. Rather than hold a
   * permanent rAF loop, this one keeps going while the measurement is changing and gives up
   * a few frames after it settles — so it costs nothing once the header has arrived.
   */
  trackHeadOffset() {
    this.headOffsetSettled = this.syncHeadOffset() ? 0 : this.headOffsetSettled + 1;
    this.headOffsetFrame =
      this.headOffsetSettled < 5 ? requestAnimationFrame(this.trackHeadOffset) : null;
  }

  /**
   * Park the bar against whatever the site header is actually covering right now.
   *
   * A fixed offset cannot work, because the header is not always there. theme.js only
   * makes it sticky past 250px of scroll, and even then it starts at
   * `translate3d(0, -100%, 0)` and slides down over 0.4s. A constant offset holds the bar
   * that far down the whole time, so everything between the top of the viewport and the
   * bar is bare page background until the header catches up — which is the gap this
   * measurement removes.
   *
   * The header's own bottom edge answers every case at once: below zero while it is
   * scrolled away or still translated out of view, rising through the slide, and its
   * settled height once it arrives. Clamping at zero turns "not there" into "sit at the
   * top", and covers the pages and breakpoints where the header never sticks at all.
   */
  syncHeadOffset() {
    const bottom = this.siteHeader ? this.siteHeader.getBoundingClientRect().bottom : 0;
    const offset = Math.max(0, Math.round(bottom));
    // Writing on every frame would invalidate style and force the next frame's read into a
    // synchronous layout; the value only actually moves while the header is animating.
    if (offset === this.headOffset) return false;
    this.headOffset = offset;
    this.headBar.style.setProperty('--comparisonTableHeadOffset', offset + 'px');
    return true;
  }

  /**
   * Pin the bar's columns to the widths the table worked out for itself.
   *
   * The two tables cannot share a column algorithm, and on mobile they would not agree on
   * their own: the table is `table-layout: auto`, so its columns are sized by all of the
   * content, while the bar holds only the head row. So the table stays the one that
   * decides, and its measurements are copied across. Fractional widths are kept — rounding
   * here is what shows up as columns a pixel out of line.
   */
  syncHeadColumns() {
    const visible = (root) =>
      Array.from(root.querySelectorAll('thead th, thead td')).filter(
        (cell) => !cell.classList.contains('comparison-table__cell--hidden')
      );

    const source = visible(this.scroller);
    const target = visible(this.headScroller);
    if (source.length !== target.length) return;

    this.headTable.style.width = this.table.getBoundingClientRect().width + 'px';
    source.forEach((cell, index) => {
      target[index].style.width = cell.getBoundingClientRect().width + 'px';
    });

    this.onScrollerScroll();
  }

  /** Keep the bar's slice of the table in step with the table's own sideways scroll. */
  onScrollerScroll() {
    this.headScroller.scrollLeft = this.scroller.scrollLeft;
  }

  initDragScroll() {
    const el = this.scroller;
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    el.addEventListener('mousedown', (event) => {
      isDown = true;
      el.classList.add('is-dragging');
      startX = event.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    });

    const stop = () => {
      isDown = false;
      el.classList.remove('is-dragging');
    };

    el.addEventListener('mouseleave', stop);
    el.addEventListener('mouseup', stop);

    el.addEventListener('mousemove', (event) => {
      if (!isDown) return;
      event.preventDefault();
      const x = event.pageX - el.offsetLeft;
      el.scrollLeft = scrollLeft - (x - startX) * 1.5;
    });
  }
}

customElements.define('collection-comparison-table', CollectionComparisonTable);
